import { useState } from "react";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EhrChip } from "@/components/chat/SidebarChat/DocLink/EhrChip";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import type {
  FieldState,
  WidgetField,
  WidgetFieldSource,
} from "@/domains/buildings/types/buildingWidget";
import { useAttributeHistory } from "@/hooks/buildings/useAttributeHistory";
import { useFieldEvidence } from "@/hooks/buildings/useFieldEvidence";
import { useSaveFieldValue } from "@/hooks/buildings/useSaveFieldValue";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { BuildingDataWidgetSections } from "@/lib/i18n/types/buildings-sections/building-data-widget";
import { BuildingDataInspectorHistory } from "./BuildingDataInspectorHistory";
import { BuildingDataInspectorValueEditor } from "./BuildingDataInspectorValueEditor";
import { EvidencePanel } from "./EvidencePanel";
import { IconButton } from "./FieldRowControls";
import { getEnumRule, localizeEnumValue } from "./getEnumRule";
import { isRedundantUserSave } from "./isRedundantSave";
import { canSaveInspectorDraft } from "./inspectorDraft";
import { useBottomRightExclusion } from "@/components/layout/useBottomRightExclusion";
import { ReasoningInfoPopover } from "./SourceGroupChip";
import { stripTrailingUnit } from "./stripTrailingUnit";
import { useInspectorPanelWidth } from "./useInspectorPanelWidth";
import { validateFieldValue } from "./validateFieldValue";

interface BuildingDataInspectorProps {
  field: WidgetField;
  groupTitle: string;
  buildingId: string;
  orgId: string | undefined;
  registryCode: string | null;
  canManage: boolean;
  onClose: () => void;
  onDocPreview: (filename: string) => void;
}

type MachineSource = Exclude<WidgetFieldSource, { kind: "user" }>;

function statusClasses(state: FieldState): string {
  if (state === "confirmed") return "bg-success/10 text-success-700";
  if (state === "pending") return "bg-warning/20 text-warning-900";
  return "bg-muted/40 text-muted-foreground";
}

function confidenceForSource(
  source: MachineSource | null | undefined,
  tw: BuildingDataWidgetSections
) {
  if (!source) return { level: 0, label: "—" };
  if (source.kind === "ehr" || source.confidence === "high") {
    return { level: 3, label: tw.confidenceHigh };
  }
  if (source.confidence === "medium") return { level: 2, label: tw.confidenceMedium };
  if (source.confidence === "low") return { level: 1, label: tw.confidenceLow };
  return { level: 0, label: "—" };
}

function stateLabel(state: FieldState, tw: BuildingDataWidgetSections): string {
  if (state === "confirmed") return tw.statusConfirmed;
  if (state === "pending") return tw.statusNeedsReview;
  return tw.statusMissing;
}

function machineSourcesFor(field: WidgetField): MachineSource[] {
  return field.sources.filter((source): source is MachineSource => source.kind !== "user");
}

function saveInspectorValue(
  mutation: ReturnType<typeof useSaveFieldValue>,
  canManage: boolean,
  field: WidgetField,
  value: string | null,
  previousValue: string | null
) {
  if (!canManage) return;
  // `data.user_data` is append-only and is the history table, so a write that
  // changes nothing still reads as a real edit in both timelines. The Save
  // button already disables on an unchanged draft; this closes the other ways
  // in — the evidence list's ✓ and "Save anyway".
  if (isRedundantUserSave(field, value)) return;
  mutation.mutate({
    attributeKey: field.id,
    attributeValue: typeof value === "string" ? value.trim() : null,
    previousValue,
    offerUndo: true,
  });
}

function previousUserValue(field: WidgetField): string | null {
  return field.state === "confirmed" ? field.value : "";
}

interface HeaderProps {
  field: WidgetField;
  groupTitle: string;
  status: string;
  closeLabel: string;
  onClose: () => void;
}

function InspectorHeader(props: HeaderProps) {
  const { field, groupTitle, status, closeLabel, onClose } = props;
  return (
    <div className="flex items-start gap-3 border-b border-border px-5 py-4">
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {groupTitle}
        </div>
        <h2 className="mt-1 text-lg font-semibold leading-6 text-foreground">{field.label}</h2>
        <span
          className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusClasses(field.state)}`}
        >
          {status}
        </span>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label={closeLabel}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <X size={16} />
      </button>
    </div>
  );
}

function InspectorActions(props: {
  disabled: boolean;
  canReject: boolean;
  canAccept: boolean;
  tw: BuildingDataWidgetSections;
  onReject: () => void;
  onAccept: () => void;
}) {
  if (props.disabled) return null;
  return (
    <div className="sticky bottom-0 flex shrink-0 gap-2 border-t border-border bg-background px-5 py-3">
      <Button
        variant="outline"
        className="flex-1 border-destructive/30 text-destructive hover:bg-destructive/10"
        disabled={!props.canReject}
        onClick={props.onReject}
      >
        <X size={14} />
        {props.tw.reject}
      </Button>
      <Button className="flex-1" disabled={!props.canAccept} onClick={props.onAccept}>
        <Check size={14} />
        {props.tw.accept}
      </Button>
    </div>
  );
}

interface InspectorBodyProps {
  field: WidgetField;
  groupTitle: string;
  machineSources: MachineSource[];
  registryCode: string | null;
  evidence: ReturnType<typeof useFieldEvidence>;
  history: ReturnType<typeof useAttributeHistory>;
  draft: string;
  enumRule: ReturnType<typeof getEnumRule>;
  canManage: boolean;
  canSave: boolean;
  isSaving: boolean;
  /** Pending with disagreeing sources and no pick yet — see the panel below. */
  unresolvedConflict: boolean;
  pendingExtremeValue: string | null;
  language: string;
  tw: BuildingDataWidgetSections;
  onDraftChange: (value: string) => void;
  onSave: () => void;
  onSaveAnyway: () => void;
  onEditExtremeValue: () => void;
  onDocPreview: (filename: string) => void;
  onConfirmValue: (value: string) => void;
}

function InspectorBody(props: InspectorBodyProps) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
      <BuildingDataInspectorValueEditor
        field={props.field}
        draft={props.draft}
        enumRule={props.enumRule}
        confidence={
          props.unresolvedConflict
            ? null
            : confidenceForSource(props.evidence.data?.source, props.tw)
        }
        canManage={props.canManage}
        canSave={props.canSave}
        isSaving={props.isSaving}
        pendingExtremeValue={props.pendingExtremeValue}
        tw={props.tw}
        onDraftChange={props.onDraftChange}
        onSave={props.onSave}
        onSaveAnyway={props.onSaveAnyway}
        onEditExtremeValue={props.onEditExtremeValue}
      />
      {props.machineSources.length > 0 ? (
        <EvidencePanel
          field={props.field}
          sources={props.machineSources}
          enumRule={props.enumRule}
          registryCode={props.registryCode}
          canManage={props.canManage}
          tw={props.tw}
          onDocPreview={props.onDocPreview}
          onConfirmValue={props.onConfirmValue}
        />
      ) : null}
      <BuildingDataInspectorHistory
        entries={props.history.data}
        field={props.field}
        language={props.language}
        isLoading={props.history.isLoading}
        isError={props.history.isError}
        tw={props.tw}
        onRetry={() => void props.history.refetch()}
      />
    </div>
  );
}

function BuildingDataInspectorContent(props: BuildingDataInspectorProps) {
  const { field, groupTitle, buildingId, orgId, registryCode, canManage, onClose, onDocPreview } =
    props;
  const { t, language } = useLanguage();
  const tw = t.buildings.buildingDataWidget;
  // Several sources reported this field and they disagree, with nobody having
  // picked yet. `field.value` is only the precedence winner (user > ehr >
  // extractor > ifc) — prefilling it into the Value box, and offering
  // Accept/Reject over it, had the panel choosing a side on the user's behalf
  // and dressing that choice up as the answer. The candidates are all laid out
  // under SOURCE EVIDENCE with a ✓ each; the Value box stays blank for a value
  // the user would rather type themselves.
  const unresolvedConflict = field.state === "pending" && field.hasConflict;
  const [draft, setDraft] = useState(unresolvedConflict ? "" : field.value);
  const [pendingExtremeValue, setPendingExtremeValue] = useState<string | null>(null);
  const saveMutation = useSaveFieldValue(buildingId, orgId);
  const evidence = useFieldEvidence(buildingId, field, field.value, true);
  const history = useAttributeHistory(buildingId, orgId, field.id, true);
  const machineSources = machineSourcesFor(field);
  const enumRule = getEnumRule(field.validation, language);
  const normalizedDraft = field.unit ? stripTrailingUnit(draft) : draft.trim();
  const canSaveDraft = canSaveInspectorDraft(field.state, normalizedDraft, enumRule);
  const persist = (value: string | null, previousValue = previousUserValue(field)) =>
    saveInspectorValue(saveMutation, canManage, field, value, previousValue);
  const saveDraft = () => {
    if (!canSaveDraft) return;
    if (!enumRule && !validateFieldValue(normalizedDraft, field.validation).ok) {
      setPendingExtremeValue(normalizedDraft);
      return;
    }
    persist(normalizedDraft);
  };
  const changeDraft = (value: string) => {
    setDraft(value);
    setPendingExtremeValue(null);
  };
  // Confirming a candidate from the evidence list commits that value outright,
  // the same route the row's conflict resolver takes — but through the same
  // unit-stripping and range check `saveDraft` applies, so an out-of-spec
  // finding still stops at the warning instead of being written on one click.
  const confirmValue = (value: string) => {
    const cleaned = field.unit ? stripTrailingUnit(value) : value.trim();
    if (!cleaned || saveMutation.isPending) return;
    setDraft(cleaned);
    if (!enumRule && !validateFieldValue(cleaned, field.validation).ok) {
      setPendingExtremeValue(cleaned);
      return;
    }
    persist(cleaned);
  };
  return (
    <>
      <InspectorHeader
        field={field}
        groupTitle={groupTitle}
        status={stateLabel(field.state, tw)}
        closeLabel={tw.closeInspector}
        onClose={onClose}
      />
      <InspectorBody
        field={field}
        groupTitle={groupTitle}
        machineSources={machineSources}
        registryCode={registryCode}
        evidence={evidence}
        history={history}
        draft={draft}
        enumRule={enumRule}
        canManage={canManage}
        canSave={canSaveDraft}
        isSaving={saveMutation.isPending}
        unresolvedConflict={unresolvedConflict}
        pendingExtremeValue={pendingExtremeValue}
        language={language}
        tw={tw}
        onDraftChange={changeDraft}
        onSave={saveDraft}
        onSaveAnyway={() => {
          if (pendingExtremeValue !== null) persist(pendingExtremeValue);
        }}
        onEditExtremeValue={() => setPendingExtremeValue(null)}
        onDocPreview={onDocPreview}
        onConfirmValue={confirmValue}
      />
      <InspectorActions
        disabled={!canManage || field.state === "confirmed" || unresolvedConflict}
        canReject={!saveMutation.isPending && field.state !== "empty"}
        canAccept={!saveMutation.isPending && canSaveDraft}
        tw={tw}
        onReject={() => persist(null)}
        onAccept={saveDraft}
      />
    </>
  );
}

/**
 * The attribute panel's chrome: width, border, and the left-edge drag handle,
 * mirroring the PM issue detail panel. `useInspectorPanelWidth` owns the width
 * policy and the clamp that keeps the field list on screen; the handle is
 * keyboard-operable — the hook supplies the separator's tabIndex, arrow-key
 * handling and `aria-value*` attributes.
 *
 * Two things live out here rather than in the content below, and both matter:
 *
 * - The width state. The content remounts on every field change (the key
 *   below), which would otherwise re-read localStorage per selection and drop
 *   a width the 200ms persistence debounce had not written yet — clicking
 *   through fields would quietly reset the panel.
 * - `useBottomRightExclusion`. Unlike the issue panel this one is in flow
 *   rather than a fixed overlay, but it still owns the viewport's right edge,
 *   so the chat pill has to clear it — at 65% width it would otherwise sit on
 *   top of the panel's Accept/Reject bar. It publishes `settledWidth`, not the
 *   live one: the exclusion writes a custom property on the root element, so
 *   following a drag pixel by pixel would invalidate layout globally.
 */
export function BuildingDataInspector(props: BuildingDataInspectorProps) {
  const { t } = useLanguage();
  const { panelRef, width, settledWidth, dragging, hover, handleProps } = useInspectorPanelWidth();
  useBottomRightExclusion("building-data-inspector", settledWidth);

  return (
    <aside
      ref={panelRef}
      className="relative flex h-full min-h-0 shrink-0 flex-col border-l border-border bg-background"
      style={{ width }}
    >
      {/* Left-edge resize handle: a wide hit area with a subtle accent line on
          hover/drag, the same affordance the issue detail panel offers. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t.buildings.buildingDataWidget.resizeInspector}
        {...handleProps}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 6,
          cursor: "col-resize",
          zIndex: 2,
          touchAction: "none",
          background: dragging || hover ? "hsl(var(--primary) / 0.4)" : "transparent",
          transition: "background 120ms",
        }}
      />
      <BuildingDataInspectorContent key={`${props.field.id}:${props.field.value}`} {...props} />
    </aside>
  );
}
